

import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { GetCommonDomainHierarchy } from "../../queries/appQueries";
import "./OverallReport.css";
import { Filter, RotateCcw, Download } from "lucide-react";
import Tabs from "../ReportTab/Tabs";
import OverviewPage from "./OverviewPage";
import AssetsDetails from "./AssetsDetails";
import RuleDetails from "./RuleDetails";
import AccessFilterDrawer from "../ActionPage/AccessFilterDrawer";
import exportService from "../../services/exportService";
import { useExport } from "../../context/ExportContext";
import AppliedFilters from "../../component/common/AppliedFilters";

const OverallReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters now store arrays for multi-select support
  const [filters, setFilters] = useState({
    parentDomain: [],
    domain: [],
    subDomain: [],
    product: [],
    startDate: "",
    endDate: "",
  });

  const [activeTab, setActiveTab] = useState(
    searchParams.get("activeTab") || "overview"
  );

  const [filterOptionsTree, setFilterOptionsTree] = useState({ parentDomains: [] });
  const { data: domainHierarchyData } = GetCommonDomainHierarchy();
  const [showFilters, setShowFilters] = useState(false);
  const [assetActiveFilters, setAssetActiveFilters] = useState({});
  const [ruleActiveFilters, setRuleActiveFilters] = useState({});
  const [isAssetExporting, setIsAssetExporting] = useState(false);
  const [isRuleExporting, setIsRuleExporting] = useState(false);
  const { startExportPolling } = useExport();

  const handleExport = async () => {
    const userId = localStorage.getItem("userId");
    const isAssets = activeTab === "assets";
    const label = isAssets ? "Asset table" : "Rule table";
    const setExporting = isAssets ? setIsAssetExporting : setIsRuleExporting;

    setExporting(true);
    try {
      const { job_id } = isAssets
        ? await exportService.triggerAssetExport(filters, assetActiveFilters, userId)
        : await exportService.triggerRuleExport(filters, ruleActiveFilters, userId);

      startExportPolling(job_id, label, {
        onComplete: () => setExporting(false),
        onError: () => setExporting(false),
      });
    } catch {
      setExporting(false);
    }
  };

  const [localFilters, setLocalFilters] = useState({
    parentDomain: [],
    domain: [],
    subDomain: [],
    product: [],
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    setSearchParams({
      activeTab,
      parentDomain: filters.parentDomain.join(",") || "",
      domain: filters.domain.join(",") || "",
      subDomain: filters.subDomain.join(",") || "",
      product: filters.product.join(",") || "",
    });
  }, [filters, activeTab, setSearchParams]);

  // Seed filter tree from common domain hierarchy endpoint on mount
  useEffect(() => {
    if (domainHierarchyData?.parentDomains) {
      handleFilterOptionsFromTab(domainHierarchyData.parentDomains);
    }
  }, [domainHierarchyData]);

  // Sync localFilters with filters when drawer opens
  // (handled inline in the button click handler below)

  const handleResetFilters = () => {
    setLocalFilters({ parentDomain: [], domain: [], subDomain: [], product: [], startDate: "", endDate: "" });
    setFilters({
      parentDomain: [],
      domain: [],
      subDomain: [],
      product: [],
      startDate: "",
      endDate: "",
    });
  };

  const handleApplyFilters = (pendingFilters) => {
    const pdIdToN = {}, dIdToN = {}, sdIdToN = {};
    (domainHierarchyData?.parentDomains || []).forEach((pd) => {
      pdIdToN[pd.parent_domain_id] = pd.parent_domain;
      (pd.domains || []).forEach((d) => {
        dIdToN[d.domain_id] = d.domain;
        (d.sub_domains || []).forEach((sd) => {
          sdIdToN[sd.sub_domain_id] = sd.sub_domain;
        });
      });
    });
    // Persist committed IDs so drawer restores correctly on reopen
    setLocalFilters(pendingFilters);
    setFilters({
      parentDomain: (pendingFilters.parentDomain || []).map((id) => pdIdToN[id] || id),
      domain: (pendingFilters.domain || []).map((id) => dIdToN[id] || id),
      subDomain: (pendingFilters.subDomain || []).map((id) => sdIdToN[id] || id),
      product: pendingFilters.product || [],
      startDate: pendingFilters.startDate || "",
      endDate: pendingFilters.endDate || "",
    });
    setShowFilters(false);
  };

  const filteredDomains = useMemo(() => {
    const parents = filterOptionsTree.parentDomains || [];
    if (filters.parentDomain && filters.parentDomain.length > 0) {
      return parents
        .filter((pd) => filters.parentDomain.includes(pd.parent_domain))
        .flatMap((pd) => pd.domains || []);
    }
    return parents.flatMap((pd) => pd.domains || []);
  }, [filterOptionsTree, filters.parentDomain]);

  const filteredSubDomains = useMemo(() => {
    if (filters.domain && filters.domain.length > 0) {
      return filteredDomains
        .filter((d) => filters.domain.includes(d.domain))
        .flatMap((d) => d.sub_domains || []);
    }
    return filteredDomains.flatMap((d) => d.sub_domains || []);
  }, [filteredDomains, filters.domain]);

  const filteredProducts = useMemo(() => {
    if (filters.subDomain && filters.subDomain.length > 0) {
      return filteredSubDomains
        .filter((sd) => filters.subDomain.includes(sd.sub_domain))
        .flatMap((sd) => sd.products || []);
    }
    return filteredSubDomains.flatMap((sd) => sd.products || []);
  }, [filteredSubDomains, filters.subDomain]);

  const handleFilterOptionsFromTab = (parentDomainsArray = []) => {
    setFilterOptionsTree({ parentDomains: parentDomainsArray });
    // Keep existing filter selections (arrays maintained as-is)
  };

  return (
   <div className="dash-wrapper flex flex-col dashboard-dashboard poppins-regular">
  <div className="flex justify-between items-center gap-6 pt-4 pl-4 pr-4 pb-3">
    <div>
      <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
    </div>

    <div className="flex items-center gap-2">
      {(activeTab === "assets" || activeTab === "rules") && (() => {
        const exporting = activeTab === "assets" ? isAssetExporting : isRuleExporting;
        const label = activeTab === "assets" ? "Export Asset Table" : "Export Rule Table";
        return (
          <button
            onClick={handleExport}
            disabled={exporting}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[34px] whitespace-nowrap ${
              exporting
                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                : "bg-[#bb1ccc] text-white border-[#bb1ccc] "
            }`}
          >
            <Download className="h-4 w-4" />
            {exporting ? "Exporting..." : label}
          </button>
        );
      })()}
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[45px] whitespace-nowrap bg-[#bb1ccc] text-white "
      >
        <Filter className="h-4 w-4" />
        {showFilters ? "Hide Filters" : "Show Filters"}
      </button>
      <button
        onClick={handleResetFilters}
       className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[45px] whitespace-nowrap bg-[#bb1ccc] text-white "
      >
        <RotateCcw className="h-4 w-4" />
        Reset
      </button>
    </div>
  </div>

  {(() => {
    const chips = [
      ...(filters.parentDomain || []).map((v) => ({ label: "Parent Domain", value: v })),
      ...(filters.domain || []).map((v) => ({ label: "Domain", value: v })),
      ...(filters.subDomain || []).map((v) => ({ label: "Sub Domain", value: v })),
      ...(filters.product || []).map((v) => ({ label: "Product", value: v })),
      ...(filters.startDate ? [{ label: "Start Date", value: filters.startDate }] : []),
      ...(filters.endDate ? [{ label: "End Date", value: filters.endDate }] : []),
    ];
    return chips.length > 0 ? (
      <div className="px-4 pb-2">
        <AppliedFilters chips={chips} />
      </div>
    ) : null;
  })()}

  <div>
    {activeTab === "overview" && (
      <OverviewPage
        filters={filters}
        setFilters={setFilters}
        onFilterOptionsChange={handleFilterOptionsFromTab}
      />
    )}
    {activeTab === "assets" && (
      <AssetsDetails
        filters={filters}
        setFilters={setFilters}
        onFilterOptionsChange={handleFilterOptionsFromTab}
        onActiveSearchParamsChange={setAssetActiveFilters}
      />
    )}
    {activeTab === "rules" && (
      <RuleDetails
        filters={filters}
        setFilters={setFilters}
        onFilterOptionsChange={handleFilterOptionsFromTab}
        onActiveSearchParamsChange={setRuleActiveFilters}
      />
    )}
  </div>

  {showFilters && (
    <AccessFilterDrawer
      filters={domainHierarchyData || filterOptionsTree}
      appliedFilters={localFilters}
      onClose={() => setShowFilters(false)}
      onApply={handleApplyFilters}
      onReset={handleResetFilters}
      showAccessCategory={false}
    />
  )}

</div>

  );
};

export default OverallReport;
